import { Component, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalNotifierService, GlobalInfoService, AuthenticationService } from '@app/core';
import { ActividadCalendario, BaseControl, ConfirmComponent } from '@app/shared';

import { CalendarService } from './shared/calendar.service';
import { BsModalRef } from 'ngx-bootstrap';
import { CalendarConfig } from './shared/calendar.config.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { LocalDataSource } from 'ng2-smart-table';
import { CalendarFormService } from './shared/calendar.forms.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

@Component({
  selector: 'iw-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [
    CalendarService,
    CalendarFormService
  ]
})
export class CalendarComponent implements OnInit, OnDestroy {
  // Define cuando se esta cargando el calendario de actividades
  isLoading = false;
  // controles dinamicos de el formulario
  controls: BaseControl<any>[] = [];
  // informacion del calendario
  calendarData: ActividadCalendario[] = [];
  // Datos de la tabla
  data: Array<any> = [];
  // Datos para llenar la tabla
  sourceData: LocalDataSource;
  // Configuración de la tabla
  settings = {};
  // Referencia de la modal
  modalRef: BsModalRef;
  // Contiene la data de la fila seleccionada
  rowData: ActividadCalendario;
  // Datos del formulario
  dataReject: any;
  // Destruye y gestiona el memory leak
  private destroy: Subject<void> = new Subject<void>();
  // Modal de formulario de rechazo de tarea
  @ViewChild('templateReject') private templateReject: TemplateRef<any>;
  // Modal de acciones a la tarea asignada
  @ViewChild('templateAccion') private templateAccion: TemplateRef<any>;
  // Referencia al componente de confirmación
  @ViewChild('confirmAction') private confirmAction: ConfirmComponent;

  constructor(
    private router: Router,
    private cs: CalendarService,
    private gns: GlobalNotifierService,
    private gis: GlobalInfoService,
    private as: AuthenticationService,
    private modalService: BsModalService,
    private cfs: CalendarFormService,
    private toastr: ToastrService,
  ) { }

  ngOnInit() {
    // Actualizar título de la vista
    this.gis.set({ title: 'Calendario de actividades' });
    // Cargar el calendario de actividades
    this.loadCalendar();
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * Controla el evento clic en la fila de la tabla.
   *
   * @param {any} row
   * @memberof CalendarComponent
   */
  onCellClick(row: any) {
    this.rowData = row.data;
    this.modalRef = this.modalService.show(this.templateAccion, { class: 'modal-lg'});
  }

  /**
   * Consulta el tramite seleccionado.
   *
   * @param {*} row
   * @memberof CalendarComponent
   */
  goTask() {
    this.modalRef.hide();
    this.gns.set<ActividadCalendario>({
      source: 'CalendarComponent',
      destiny: 'FlowComponent',
      action: 'showFlow',
      eventTriggered: 'click',
      data: <ActividadCalendario>this.rowData
    }, true);
    this.router.navigate(['/home']);
  }

  /**
   * Abre el modal de rechazo de tarea
   *
   * @param {ActividadCalendario} data
   * @memberof CalendarComponent
   */
  openModalReject() {
    if (this.rowData.rechazable) {
      this.controls = this.cfs.getFormReject();
      this.modalRef.hide();
      this.modalRef = this.modalService.show(this.templateReject, { class: 'modal-lg'});
    } else {
      this.toastr.warning(
        'No es posible realizar esta acción ya que la tarea no puede ser rechazada',
        'Acción no permitida'
      );
    }
  }

  /**
   * Abre el modal de confirmacion para el rechazo
   *
   * @param {*} data
   * @memberof CalendarComponent
   */
  confirm(data: any) {
    this.dataReject = data;
    this.confirmAction.open();
  }

  /**
   * Controla la accion de la confirmacion en el rechazo
   *
   * @param {*} res
   * @memberof CalendarComponent
   */
  action(res: any) {
    if (res) {
      this.cs.rejectTask(this.rowData.id, this.dataReject.txtMotivo).subscribe(resR => {
        if (resR) {
          this.loadCalendar();
          this.modalRef.hide();
        }
      });
    }
    this.dataReject = null;
  }

  /**
   * Realiza la consulta de las actividades asignadas por usuario en sesion
   *
   * @private
   * @memberof CalendarComponent
   */
  private loadCalendar() {
    const user = this.as.getCredentials();
    this.isLoading = true;
    this.data = [];
    this.cs.getDataCalendar(user.user_id).then(data => {
      this.calendarData = data;
      this.loadDinamicFieldFromTable();
    }).catch(() => {
      // Se inicia la configuracion de la tabla
      const configTable = new CalendarConfig();
      this.settings = configTable.settings;
      this.calendarData = [];
      this.loadTableActivities();
    });
  }

  /**
   * Genera los campos dinamicos de el calendario de actividades en la tabla
   *
   * @private
   * @memberof CalendarComponent
   */
  private loadDinamicFieldFromTable() {
    const configTable = new CalendarConfig();
    this.settings = {};
    if (this.calendarData && this.calendarData.length) {
      if (this.calendarData[0].campos && this.calendarData[0].campos.length) {
        this.calendarData.forEach(cd => {
          cd.campos.sort((a, b) => a.orden - b.orden);
        });
        this.calendarData[0].campos.forEach(field => {
          configTable.generateColumn(field.orden + ' ', field.nombre_parametro);
        });
      }
    }
    this.settings = configTable.settings;
    this.loadTableActivities();
  }

  /**
   * Carga la informacion de cada tarea asignada para ser visaulizada en la tabla de calendario
   *
   * @private
   * @memberof CalendarComponent
   */
  private loadTableActivities() {
    this.data = [];
    if (this.calendarData && this.calendarData.length) {
      this.calendarData.forEach(activity => {
        const d = {};
        Object.keys(activity).forEach(key => {
          if (key !== 'campos') {
            d[key] = activity[key];
          }
        });
        if (activity.campos && activity.campos.length) {
          activity.campos.forEach(field => {
            d[field.orden + ' '] = field.valor;
            if (field['formato_fecha']) {
              d['formato_fecha'] = field['formato_fecha'];
            }
          });
        }
        this.data.push(d);
      });
    }
    this.sourceData = new LocalDataSource(this.data);
    this.sourceData.refresh();
    this.isLoading = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/calendar/calendar.component.ts